import { X, Info, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { useAreaActions, useAreaState } from '../stores/useAreaStore'

import { CreateAreaRequest } from '@/services/WarehouseV2Service'
import { useUIStore } from '@/stores/useUIStore'

interface CreateZoneFormProps {
  isOpen: boolean
  onClose: () => void
}

interface FormData {
  name: string
  code: string
  description: string
  positionCount: number
  positionPrefix: string
}

const CreateZoneForm: React.FC<CreateZoneFormProps> = ({ isOpen, onClose }) => {
  const { creating } = useAreaState()
  const { createAreaWithPositions } = useAreaActions()
  const { showError, showSuccess } = useUIStore()

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid, touchedFields }
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      code: '',
      description: '',
      positionCount: 10,
      positionPrefix: ''
    }
  })

  const watchedCode = watch('code')
  const watchedName = watch('name')
  const watchedPositionCount = watch('positionCount')
  const watchedPositionPrefix = watch('positionPrefix')
  const watchedDescription = watch('description')

  useEffect(() => {
    if (watchedCode && !touchedFields.positionPrefix) {
      setValue('positionPrefix', `${watchedCode}-`)
    }
  }, [watchedCode, touchedFields.positionPrefix, setValue])

  const onSubmit = async (data: FormData) => {
    const apiData: CreateAreaRequest = {
      name: data.name.trim(),
      code: data.code.toUpperCase(),
      description: data.description || '',
      positionCount: data.positionCount,
      positionPrefix: data.positionPrefix
    }
    const { success, message } = await createAreaWithPositions(apiData)
    if (success) {
      reset()
      onClose()
      showSuccess(message)
    } else {
      showError(message)
    }
  }

  const getInputClassName = (fieldName: keyof FormData) => {
    const hasError = errors[fieldName]
    const hasSuccess = touchedFields[fieldName] && !hasError && watch(fieldName)
    return `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200 ${
      hasError
        ? 'border-red-300 focus:ring-red-500'
        : hasSuccess
        ? 'border-green-300 focus:ring-green-500'
        : 'border-gray-300 focus:ring-blue-500'
    }`
  }

  const renderStatusIcon = (fieldName: keyof FormData) => {
    const hasError = errors[fieldName]
    const hasSuccess = touchedFields[fieldName] && !hasError && watch(fieldName)
    if (hasSuccess)
      return (
        <CheckCircle className="absolute right-3 top-2.5 h-4 w-4 text-green-500" />
      )
    if (hasError)
      return (
        <AlertCircle className="absolute right-3 top-2.5 h-4 w-4 text-red-500" />
      )
    return null
  }

  const renderError = (fieldName: keyof FormData) => {
    const error = errors[fieldName]
    return error ? (
      <p className="mt-1 text-sm text-red-600">{error.message}</p>
    ) : null
  }

  const padZero = (num: number, length: number) => {
    return num.toString().padStart(length, '0')
  }

  const getPositionCodeExample = () => {
    const prefix =
      watchedPositionPrefix || (watchedCode ? `${watchedCode}-` : '前缀-')
    return `
      ${prefix}${padZero(1, watchedPositionCount.toString().length)}, 
      ${prefix}${padZero(2, watchedPositionCount.toString().length)}...
    `
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={!creating ? onClose : undefined}
        />

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="bg-white px-6 py-6">
              {/* 标题 */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  新建库区
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    reset()
                    onClose()
                  }}
                  disabled={creating}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* 表单内容 */}
              <div className="space-y-6">
                {/* 第一行：库区名称和库区编码 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      库区名称 <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        {...register('name', {
                          required: '库区名称不能为空',
                          maxLength: {
                            value: 50,
                            message: '库区名称不能超过50个字符'
                          },
                          validate: (value) =>
                            value?.trim().length > 0 || '库区名称不能为空'
                        })}
                        className={getInputClassName('name')}
                        placeholder="例如：A区、B区"
                        disabled={creating}
                      />
                      {renderStatusIcon('name')}
                    </div>
                    {renderError('name')}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      库区编码 <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        {...register('code', {
                          required: '库区编码不能为空',
                          maxLength: {
                            value: 20,
                            message: '库区编码不能超过20个字符'
                          },
                          validate: (value) => {
                            if (!value?.trim()) return '库区编码不能为空'
                            if (!/^[A-Z0-9]+$/i.test(value.trim()))
                              return '库区编码只能包含大写字母和数字'
                            return true
                          }
                        })}
                        className={getInputClassName('code')}
                        placeholder="例如：A01、B02"
                        style={{ textTransform: 'uppercase' }}
                        disabled={creating}
                      />
                      {renderStatusIcon('code')}
                    </div>
                    {renderError('code')}
                    <p className="mt-1 text-xs text-gray-500">
                      仅支持大写字母和数字，将自动转为大写
                    </p>
                  </div>
                </div>

                {/* 第二行：库位数量和库位前缀 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      库位数量 <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        {...register('positionCount', {
                          required: '库位数量不能为空',
                          min: { value: 1, message: '库位数量必须大于0' },
                          max: { value: 1000, message: '库位数量不能超过1000' },
                          valueAsNumber: true
                        })}
                        className={getInputClassName('positionCount')}
                        min="1"
                        max="1000"
                        placeholder="10"
                        disabled={creating}
                      />
                      {renderStatusIcon('positionCount')}
                    </div>
                    {renderError('positionCount')}
                    <p className="mt-1 text-xs text-gray-500">
                      将自动生成指定数量的库位（1-1000）
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                      库位前缀 <Info className="ml-1 h-4 w-4 text-gray-400" />
                    </label>
                    <input
                      type="text"
                      {...register('positionPrefix')}
                      className={getInputClassName('positionPrefix')}
                      placeholder={
                        watchedCode ? `${watchedCode}` : '例如：A-01'
                      }
                      disabled={creating}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      库位编码格式：{getPositionCodeExample()}
                    </p>
                  </div>
                </div>

                {/* 第三行：描述 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    描述（可选）
                  </label>
                  <textarea
                    {...register('description', {
                      maxLength: {
                        value: 200,
                        message: '描述不能超过200个字符'
                      }
                    })}
                    rows={3}
                    className={getInputClassName('description')}
                    placeholder="为库区添加详细描述，便于管理识别"
                    disabled={creating}
                  />
                  {renderError('description')}
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">
                      详细描述有助于库区管理和识别
                    </p>
                    <p className="text-xs text-gray-400">
                      {watchedDescription?.length || 0}/200
                    </p>
                  </div>
                </div>

                {/* 预览信息 */}
                {watchedName && watchedCode && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-900 mb-3 flex items-center">
                      <Info className="h-4 w-4 mr-2" />
                      预览信息
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <div>
                          <span className="text-blue-700 font-medium">
                            库区名称：
                          </span>
                          <span className="text-blue-800">{watchedName}</span>
                        </div>
                        <div>
                          <span className="text-blue-700 font-medium">
                            库区编码：
                          </span>
                          <span className="text-blue-800 font-mono">
                            {watchedCode}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <span className="text-blue-700 font-medium">
                            库位数量：
                          </span>
                          <span className="text-blue-800">
                            {watchedPositionCount || 0} 个
                          </span>
                        </div>
                        <div>
                          <span className="text-blue-700 font-medium">
                            编码示例：
                          </span>
                          <span className="text-blue-800 font-mono text-xs">
                            {getPositionCodeExample()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse gap-3">
              <button
                type="submit"
                disabled={creating || !isValid}
                className={`w-full sm:w-auto inline-flex justify-center items-center px-6 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 ${
                  creating || !isValid
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 shadow-sm hover:shadow-md'
                }`}
              >
                {creating ? (
                  <>
                    <Loader2 className="animate-spin h-4 w-4 mr-2" />
                    创建中...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    创建库区
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  reset()
                  onClose()
                }}
                disabled={creating}
                className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center items-center px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all duration-200"
              >
                <X className="h-4 w-4 mr-2" />
                取消
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CreateZoneForm
